import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useDistricts, useProvinces } from '../../lib/geo'
import type { Organization } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, Input, Select, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'

/* Faithful port of anansi/app/templates/admin/create_org.html + its view
 * admin/routes.py:create_organization(). The Jinja original branches into
 * approval_service.submit_request() when the creator needs approval
 * (any admin who isn't super_admin); that branch is skipped entirely here -
 * every caller who reaches this React admin portal already is super_admin
 * (see roles.ts:portalFor()), so an org is always created directly, exactly
 * like the Jinja view's own super_admin path does.
 *
 * Submits to POST /api/v1/organizations (api_create_org), not
 * POST /api/v1/admin/orgs (api_admin_org_create): the former already
 * accepts mobile_money and turns a duplicate name/registration_no
 * (DuplicateOrganisationError) into a clean 409 the way the Jinja original's
 * own try/except does; the latter has no mobile_money field and would
 * surface a duplicate as a bare 400 instead. */

const ORG_TYPES = [
  { value: 'cooperative', label: 'Cooperative' },
  { value: 'company', label: 'Company' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'family', label: 'Family Enterprise' },
]

interface CreateOrgResult { organization: Organization }

function submitError(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback
}

export default function AdminCreateOrganization() {
  const navigate = useNavigate()
  const provinces = useProvinces()
  const [regionId, setRegionId] = useState('')
  const [districtId, setDistrictId] = useState('')
  const districts = useDistricts(regionId)

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<CreateOrgResult>('/organizations', body),
    onSuccess: (data) => navigate(`/organizations/${data.organization.id}`),
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      name: String(form.get('name') ?? '').trim(),
      type: String(form.get('type') ?? 'cooperative'),
      registration_no: String(form.get('registration_no') ?? '').trim(),
      tax_id: String(form.get('tax_id') ?? '').trim(),
      mobile_money: String(form.get('mobile_money') ?? '').trim(),
      region_id: regionId,
      district_id: districtId,
      address: String(form.get('address') ?? '').trim(),
    })
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Create organisation</h1>
          <p className="page-head__sub">Register a new company or cooperative directly.</p>
        </div>
      </div>

      <Card>
        <CardHeader title="Organisation details" />
        <CardBody>
          <form onSubmit={onSubmit} className="stack">
            {create.isError && (
              <Alert tone="danger">{submitError(create.error, 'Could not create that organisation.')}</Alert>
            )}

            {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
            <Wizard
              steps={[
                { title: 'Identity', content: (
                  <div className="grid grid--2">
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Input label="Organisation Name" name="name" required placeholder="Copper Hills Mining Cooperative" />
                    </div>
                    <Select label="Type" name="type" required defaultValue="cooperative">
                      {ORG_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Select>
                  </div>
                ) },
                { title: 'Registration & payments', content: (
                  <div className="grid grid--2">
                    <Input label="Registration Number" name="registration_no" placeholder="REG-12345" />
                    <Input label="Tax ID" name="tax_id" placeholder="TIN-000000" />
                    <Input label="Mobile Money Account" name="mobile_money" placeholder="+260 97 000 0000" />
                  </div>
                ) },
                { title: 'Location', content: (
                  <div className="grid grid--2">
                    <Select
                      label="Region" value={regionId}
                      onChange={(e) => { setRegionId(e.target.value); setDistrictId('') }}
                    >
                      <option value="">Select region…</option>
                      {provinces.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Select>
                    <Select
                      label="District" value={districtId}
                      onChange={(e) => setDistrictId(e.target.value)} disabled={!regionId}
                    >
                      <option value="">Select district…</option>
                      {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </Select>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Textarea label="Address" name="address" rows={2} placeholder="Physical address…" />
                    </div>
                  </div>
                ) },
              ]}
              footer={(
                <div className="row">
                  <Button type="submit" loading={create.isPending}>Create Organisation</Button>
                </div>
              )}
            />
          </form>
        </CardBody>
      </Card>
    </>
  )
}
